import React, { useState, useEffect } from "react";
import Notifications from "./Notifications";
import {
  subscribeToOffline,
  subscribeToOnline,
  getNetworkOnline,
} from "../../utils/networkStatus";

const OFFLINE_HEADER = "You are offline";
const OFFLINE_MESSAGE =
  "The application is not connected to the internet. Please check your network connection and try again.";

/**
 * Detects internet connectivity loss and renders an inline error notification.
 * Placed between `<Header />` and `<main>` in App.tsx.
 *
 * ## Detection strategy
 *
 * ### Primary — Axios `ERR_NETWORK` (all browsers including Safari)
 * When any API call fails because no HTTP response was received, the Axios
 * response interceptor in `api/axios.ts` calls `reportNetworkError()`.
 * This component subscribes to that signal via the `networkStatus` pub/sub
 * module and shows the banner immediately — no polling required.
 * `reportNetworkSuccess()` is called on the next successful response and
 * triggers the recovery path here.
 * See NETWORK_ERROR_HANDLING.md §Layer 2 for the full detection flow.
 *
 * ### Secondary — browser `offline` / `online` events
 * Proactive fallback for Chrome / Firefox when the network drops before any
 * API call is made (e.g. the page just loaded and the user goes offline
 * before clicking anything).
 *
 * ## Side-effects
 * - `<main>` is hidden while offline to prevent interaction with stale content.
 * - All subscriptions and listeners are removed on unmount.
 */
const OfflineBanner: React.FC = () => {
  const [isOnline, setIsOnline] = useState<boolean>(true);

  useEffect(() => {
    const mainEl = document.querySelector<HTMLElement>("main");

    const goOffline = (): void => {
      setIsOnline(false);
      if (mainEl) mainEl.style.display = "none";
    };

    const goOnline = (): void => {
      setIsOnline(true);
      if (mainEl) mainEl.style.display = "";
    };

    // Primary: subscribe to Axios-driven ERR_NETWORK signals.
    // Fires immediately when a real API call fails with no HTTP response.
    const unsubOffline = subscribeToOffline(goOffline);
    const unsubOnline = subscribeToOnline(goOnline);

    // Secondary: browser events — fast proactive signal for Chrome / Firefox.
    globalThis.addEventListener("offline", goOffline);
    globalThis.addEventListener("online", goOnline);

    // Initial sync: reflect whatever state Axios or navigator already knows.
    if (!getNetworkOnline() || (typeof navigator !== "undefined" && !navigator.onLine)) {
      goOffline();
    }

    return (): void => {
      // Cleanup is synchronous — all subscriptions and listeners are removed
      // before any async callback can fire, so an isMounted guard is not needed.
      unsubOffline();
      unsubOnline();
      globalThis.removeEventListener("offline", goOffline);
      globalThis.removeEventListener("online", goOnline);
      if (mainEl) mainEl.style.display = "";
    };
  }, []);

  if (isOnline) return null;

  return (
    <div role="alert" className="flex! mt-20! justify-center! min-h-[70vh]!">
      <Notifications
        heading={OFFLINE_HEADER}
        messageInfo={OFFLINE_MESSAGE}
        variant="Error"
      />
    </div>
  );
};

export default OfflineBanner;

